/**
 * feat-300 — the class strings for the AI edit thread, copied VERBATIM from
 * design NOTES §3 (the mockup's script block). No new colour, font, radius or
 * spacing token: every class resolves in `tailwind.config.js` today.
 *
 * Static literal strings only — a constructed `bg-${x}` is silently purged by
 * Tailwind and renders unstyled.
 */
import { CHANGED_FIELD_ROW_CLASS } from '../../curriculumDiffPresentation';

/** = Collapsible FOCUS_RING. */
export const FOCUS = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40';

// §3.1 MicButton
export const AFFORD =
  'shrink-0 flex items-center gap-1 -mt-0.5 transition-opacity duration-100 motion-reduce:transition-none ' +
  'opacity-0 group-hover/q:opacity-100 group-focus-within/q:opacity-100 [@media(pointer:coarse)]:opacity-100';
export const ASK_BTN =
  'inline-flex items-center gap-1 h-7 pl-2 pr-2.5 rounded-full bg-white ring-1 ring-black/10 text-xs font-semibold text-ink hover:bg-cream-dark ' +
  FOCUS;
export const TYPE_BTN =
  'grid place-items-center w-7 h-7 rounded-full bg-white ring-1 ring-black/10 text-inkMuted hover:text-ink hover:bg-cream-dark ' +
  FOCUS;

// §3.2 ThreadChip
export const TCHIP =
  'shrink-0 -mt-0.5 inline-flex items-center gap-1 h-6 pl-1.5 pr-2 rounded-full text-[11px] font-bold leading-none whitespace-nowrap ' +
  FOCUS;
export const TCHIP_WORKING = 'bg-white ring-1 ring-black/10 text-inkMuted hover:text-ink';
export const TCHIP_NEEDS = 'bg-white ring-1 ring-ink/40 text-ink shadow-sm hover:bg-cream-dark';
export const TCHIP_FAILED =
  'bg-danger-surface ring-1 ring-danger-line/40 text-danger-text hover:bg-danger-em';

// §3.3 Panel shell
export const PANEL = 'mt-3 rounded-2xl bg-white ring-1 ring-black/5 shadow-sm not-italic font-normal text-ink';
export const PANEL_HEAD = 'flex items-center gap-2 px-3 md:px-4 py-2 border-b border-softBorder';
export const PANEL_TITLE = 'text-sm font-bold text-ink focus:outline-none';
export const PANEL_STATE = 'text-xs text-inkMuted truncate';
export const LOG = 'px-3 md:px-4 py-3 space-y-3';
export const META =
  'flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] font-semibold text-inkMuted';
export const MSG_YOU =
  'mt-1 rounded-xl bg-cream-dark px-3 py-2 text-sm text-ink whitespace-pre-wrap break-words';
export const MSG_AI = 'mt-1 text-sm text-ink whitespace-pre-wrap break-words';
export const COMPOSER = 'border-t border-softBorder px-3 md:px-4 py-3';
export const TEXTAREA =
  'block w-full min-h-[64px] max-h-60 overflow-y-auto resize-none rounded-xl bg-cream ring-1 ring-black/10 px-3 py-2 text-sm text-ink ' +
  'placeholder:text-inkMuted/80 focus:outline-none focus:ring-2 focus:ring-coral/40';
export const ROW = 'mt-2 flex flex-wrap items-center gap-2';
export const BTN_PRIMARY =
  'inline-flex items-center gap-1.5 h-8 px-4 rounded-full bg-coral text-white text-sm font-semibold hover:brightness-95 disabled:opacity-40 disabled:cursor-not-allowed ' +
  FOCUS;
export const BTN_SECONDARY =
  'inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-white ring-1 ring-black/10 text-sm font-semibold text-ink hover:bg-cream-dark ' +
  FOCUS;
export const BTN_QUIET =
  'inline-flex items-center gap-1 h-8 px-2.5 rounded-full text-sm font-semibold text-inkMuted hover:text-ink hover:bg-cream-dark ' +
  FOCUS;
export const HINT = 'text-[11px] text-inkMuted';
/** The `<kbd>` hint is feat-299's (NOTES §3.5), so the page has one key-hint style.
 *  Its container hides it on `pointer: coarse`. */
export { KBD } from '../InlineEditable';

// §3.4 Recording
export const REC_BAR =
  'flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl bg-cream ring-1 ring-black/10 px-3 py-2';
export const REC_TRACK = 'h-1 flex-1 min-w-[80px] rounded-full bg-cream-dark overflow-hidden';
export const REC_FILL = 'h-full bg-inkMuted rounded-full';

// §3.5 Composer notes
export const NOTE_INFO =
  'mt-2 flex items-start gap-2 rounded-xl bg-cream-dark px-3 py-2 text-xs leading-snug text-ink';
export const NOTE_DANGER =
  'mt-2 flex items-start gap-2 rounded-xl bg-danger-surface border border-danger-line/40 px-3 py-2 text-xs leading-snug text-danger-text';

// §3.6 Proposal + diff
export const PROPOSAL = 'rounded-xl ring-1 ring-black/10 bg-white';
export const PROPOSAL_HEAD = 'px-3 pt-2.5 pb-2';
export const EYEBROW = 'text-[11px] font-bold uppercase tracking-wide text-inkMuted';
export const SUMMARY = 'text-sm font-semibold text-ink whitespace-pre-wrap break-words';
export const DIFF = 'px-3 pb-1';
export const DIFF_ROW = 'py-2 border-t border-softBorder';
export const DIFF_LABEL =
  'text-[10px] uppercase tracking-wide font-bold text-inkMuted flex flex-wrap items-center gap-1.5';
export const DIFF_GRID = 'mt-1 grid grid-cols-[4.75rem_minmax(0,1fr)] gap-x-2 gap-y-1 text-sm';
export const SIDE = 'text-[11px] font-semibold text-inkMuted pt-[3px]';
export const CUR_VAL = 'text-inkMuted break-words whitespace-pre-wrap';
export const PROP_VAL = `${CHANGED_FIELD_ROW_CLASS} text-ink break-words whitespace-pre-wrap rounded-r`;
export const ANSWER_KEY_TAG =
  'text-[9px] font-bold uppercase tracking-wide rounded px-1 py-0.5 bg-cream-dark text-inkMuted';
export const PROP_ACTIONS = 'flex flex-wrap items-center gap-2 border-t border-softBorder px-3 py-2.5';
export const VARIANT_CHIP = 'inline-flex items-center rounded-md px-1.5 py-0.5 text-xs mr-1 mb-1';
export const VARIANT_KEPT = 'bg-white ring-1 ring-black/10 text-ink';
export const VARIANT_ADDED = 'bg-white ring-1 ring-ink/30 font-semibold text-ink';
export const VARIANT_REMOVED =
  'bg-white/60 ring-1 ring-black/10 text-inkMuted line-through decoration-inkMuted/70';

// §3.7 Confirm, closed, stale, failed
export const BOX_CONFIRM =
  'rounded-xl bg-cream ring-1 ring-black/10 px-3 py-2.5 text-sm text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-coral/40';
export const BOX_CLOSED =
  'flex flex-wrap items-center gap-x-2 gap-y-1 rounded-xl bg-cream px-3 py-2 text-sm text-ink focus:outline-none';
export const BOX_STALE =
  'rounded-xl bg-attention-surface border border-attention-line/40 px-3 py-2.5 text-sm text-attention-on focus:outline-none';
export const BOX_FAILED =
  'rounded-xl bg-danger-surface border border-danger-line/40 px-3 py-2.5 text-sm text-danger-text';
export const LINK_UNDERLINE = `font-semibold underline underline-offset-2 hover:no-underline rounded ${FOCUS}`;

// §3.8 History
export const HIST_BTN =
  'inline-flex items-center gap-1.5 max-w-full text-xs text-inkMuted hover:text-ink rounded py-0.5 ' + FOCUS;
export const HIST_WRAP = 'mt-2 flex flex-col items-start gap-0.5';
export const HIST_EXPANDED = 'ml-[18px] mt-1 rounded-xl ring-1 ring-black/5 bg-white/70 px-3 py-3 space-y-3';

// §3.9 Counter
export const COUNTER_BADGE =
  'grid place-items-center min-w-[20px] h-5 px-1.5 -ml-1 rounded-full bg-ink text-white text-[11px] font-bold leading-none';
